import type { Node } from "reactflow";
import type { InsightNodeData } from "./topologyNodes";
import { eniGridSlot, isEniGridMeta } from "./cardLayoutGrid";

/**
 * CTX-128 "bounded progressive disclosure" — a busy subnet's ENIs are capped
 * to a default visible count (target 6-8 after validating density against a
 * real large account; 8 chosen as the upper bound of that range pending
 * Daniel's measurement against the Dish staging dataset described in the
 * issue) and a VPC-wide visible-ENI budget stops many busy subnets from
 * together producing an unbounded canvas.
 *
 * This module is pure/presentational: it only decides which already-built
 * ReactFlow ENI nodes are visible and synthesizes a "+N ENIs" summary node
 * standing in for the rest. It never mutates `topology.nodes` and never
 * fetches anything — `InsightTopologyCanvas` is the only caller, applying
 * this after `toReactFlowNodes` and before the search/selection styling
 * pass, so expand/collapse and search-driven reveals never touch the
 * `initialNodes` reference the `fitView` effect depends on (pan/zoom stays
 * put across a disclosure change).
 */
export const DEFAULT_PER_SUBNET_ENI_LIMIT = 8;
export const DEFAULT_VPC_ENI_BUDGET = 60;

export interface EniDisclosureOptions {
	/** Default per-subnet visible-ENI cap before collapsing the rest behind "+N ENIs". */
	perSubnetLimit?: number;
	/** Default VPC-wide visible-ENI budget, applied across all of a VPC's subnets in their existing order. */
	vpcBudget?: number;
	/** Subnets the user explicitly expanded — bypasses both the per-subnet cap and the VPC budget for that subnet only (a deliberate user action is never silently re-capped). */
	expandedSubnetIds?: ReadonlySet<string>;
	/** ENI ids that must be visible regardless of caps (e.g. a search match) — force-reveals the whole owning subnet for this render, without persisting an expand state. */
	forceVisibleIds?: ReadonlySet<string>;
}

function eniAttachmentBucket(data: InsightNodeData): "attached" | "unattached" {
	return data.resolvedInstance ? "attached" : "unattached";
}

/**
 * Builds the synthetic "+N ENIs" overflow card for one subnet's hidden ENIs.
 * When the subnet's interior was re-gridded by `cardLayout.ts` (its data
 * carries an `eniGrid`), the card takes the grid's reserved free slot —
 * after the visible ENIs when collapsed, after ALL ENIs when expanded — so
 * it never sits on top of a card. A subnet still on its raw ELK interior
 * falls back to the first hidden ENI's own (parent-relative) position so it
 * visually stands in for that ENI's slot rather than floating loose.
 */
function buildOverflowNode(
	subnet: Node<InsightNodeData>,
	hidden: Node<InsightNodeData>[],
	expanded: boolean,
	slotIndex: number
): Node<InsightNodeData> {
	const subnetId = subnet.id;
	const grid = isEniGridMeta(subnet.data?.eniGrid) ? subnet.data.eniGrid : null;
	const first = hidden[0];
	const width = grid ? grid.cardWidth : typeof first.width === "number" ? first.width : 140;
	const height = grid ? grid.cardHeight : typeof first.height === "number" ? first.height : 80;
	const position = grid ? eniGridSlot(grid, slotIndex) : first.position;
	const attached = hidden.filter((eni) => eniAttachmentBucket(eni.data ?? {}) === "attached").length;
	const unattached = hidden.length - attached;
	const warning = hidden.filter((eni) => eni.data?.status === "warning").length;
	const error = hidden.filter((eni) => eni.data?.status === "error").length;

	return {
		id: `${subnetId}::eni-overflow`,
		type: "insightEniOverflow",
		parentId: subnetId,
		position,
		width,
		height,
		extent: "parent",
		style: { width, height },
		data: {
			label: `+${hidden.length} ENIs`,
			kind: "eni-overflow",
			ctxrUuids: [],
			hiddenTotal: hidden.length,
			hiddenAttached: attached,
			hiddenUnattached: unattached,
			hiddenWarning: warning,
			hiddenError: error,
			expanded,
		},
	};
}

/**
 * Applies the per-subnet cap and VPC-wide budget to an already-built
 * ReactFlow node list, returning a new node list with hidden ENIs removed
 * and "+N ENIs" overflow cards appended (each ordered after its own
 * `parentId`, satisfying ReactFlow's nesting requirement). Returns the input
 * array unchanged (same reference) when nothing needs collapsing, so callers
 * doing reference-equality checks upstream aren't defeated by a needless
 * copy.
 */
export function applyEniDisclosure(
	nodes: Node<InsightNodeData>[],
	options: EniDisclosureOptions = {}
): Node<InsightNodeData>[] {
	const perSubnetLimit = options.perSubnetLimit ?? DEFAULT_PER_SUBNET_ENI_LIMIT;
	const vpcBudget = options.vpcBudget ?? DEFAULT_VPC_ENI_BUDGET;
	const expandedSubnetIds = options.expandedSubnetIds ?? new Set<string>();
	const forceVisibleIds = options.forceVisibleIds ?? new Set<string>();

	const byId = new Map(nodes.map((node) => [node.id, node]));

	const enisBySubnet = new Map<string, Node<InsightNodeData>[]>();
	nodes.forEach((node) => {
		if (node.type !== "insightEni" || !node.parentId) return;
		const parent = byId.get(node.parentId);
		if (!parent || parent.type !== "insightSubnet") return;
		const list = enisBySubnet.get(node.parentId) ?? [];
		list.push(node);
		enisBySubnet.set(node.parentId, list);
	});

	if (enisBySubnet.size === 0) return nodes;

	// Group each VPC's subnets in their existing (server-provided) order — never re-sorted.
	const subnetsByVpc = new Map<string, string[]>();
	nodes.forEach((node) => {
		if (node.type !== "insightSubnet" || !enisBySubnet.has(node.id)) return;
		const vpcId = node.parentId ?? "__no-vpc__";
		const list = subnetsByVpc.get(vpcId) ?? [];
		list.push(node.id);
		subnetsByVpc.set(vpcId, list);
	});

	const hiddenIds = new Set<string>();
	const overflowNodes: Node<InsightNodeData>[] = [];

	subnetsByVpc.forEach((subnetIds) => {
		let budgetLeft = vpcBudget;
		subnetIds.forEach((subnetId) => {
			const enis = enisBySubnet.get(subnetId) ?? [];
			const explicitlyExpanded = expandedSubnetIds.has(subnetId);
			const hasForcedMatch = enis.some((eni) => forceVisibleIds.has(eni.id));
			const bypassCaps = explicitlyExpanded || hasForcedMatch;

			const cappedCount = Math.min(enis.length, perSubnetLimit, Math.max(budgetLeft, 0));
			const visibleCount = bypassCaps ? enis.length : cappedCount;
			if (!bypassCaps) budgetLeft -= visibleCount;

			const actuallyHidden = enis.slice(visibleCount);
			actuallyHidden.forEach((eni) => hiddenIds.add(eni.id));

			// What the overflow card describes: the ENIs actually collapsed
			// behind it right now, or — when the user explicitly expanded a
			// subnet that the default cap would otherwise have collapsed — the
			// ENIs a second click re-hides, so "Show fewer" has something
			// deterministic to collapse back to. A purely search-forced reveal
			// (not an explicit expand) never gets this persistent control —
			// clearing the search alone collapses it back, per `forceVisibleIds`'
			// per-render (non-persisted) contract.
			const cardReference = actuallyHidden.length > 0 ? actuallyHidden : explicitlyExpanded ? enis.slice(cappedCount) : [];
			if (cardReference.length === 0) return;

			// Grid slot right after whatever is actually visible: the first
			// hidden slot when collapsed, the slot after every ENI when expanded.
			const slotIndex = explicitlyExpanded ? enis.length : visibleCount;
			overflowNodes.push(buildOverflowNode(byId.get(subnetId) as Node<InsightNodeData>, cardReference, explicitlyExpanded, slotIndex));
		});
	});

	// `hiddenIds` alone isn't sufficient for the fast path: an explicitly
	// expanded subnet that the default cap would otherwise collapse still
	// needs its "Show fewer" collapse control even though nothing is
	// currently hidden (`overflowNodes` non-empty, `hiddenIds` empty).
	if (hiddenIds.size === 0 && overflowNodes.length === 0) return nodes;

	return [...nodes.filter((node) => !hiddenIds.has(node.id)), ...overflowNodes];
}
